import axios from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { API_URL, COUNTRY_CODE, VITE_AZURE_CLIENTID } from './sys';
import { msalConfig, loginRequest } from '../config/msal-config';
import store from '../store';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Create a promise to handle MSAL initialization
const msalInitializedPromise = msalInstance.initialize();

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * API Gateway Lambda Authorizer expects the Azure AD idToken (audience = SPA clientId),
 * not the Graph accessToken (audience = graph.microsoft.com). Same pattern as PIMS.
 * MSAL still uses User.Read scopes for login/silent acquisition; Authorization uses idToken.
 */
const TOKEN_SCOPES = loginRequest.scopes;

const CWID_CLAIM = 'https://bayer.com/cwid';

const handleLogout = async () => {
  try {
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error('Logout failed:', error);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  }
};

const getCwidFromAccount = (account: { idTokenClaims?: Record<string, unknown> } | null) => {
  const claims = account?.idTokenClaims;
  const cwid = claims?.[CWID_CLAIM];
  return typeof cwid === 'string' && cwid.length > 0 ? cwid : undefined;
};

// Add a request interceptor to attach the auth token and identity headers
axiosInstance.interceptors.request.use(
  async (config: any) => {
    try {
      await msalInitializedPromise;

      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        const account = accounts[0];

        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            account: account,
            scopes: [VITE_AZURE_CLIENTID + '/.default'],
          });

          // Authorizer validates Bayer Azure AD JWT — must be idToken, not Graph accessToken
          config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        } catch (silentError) {
          console.warn('Silent token acquisition failed:', silentError);
          await handleLogout();
          return Promise.reject(new Error('Authentication failed'));
        }

        const state = store.getState();
        const storeUserId = state?.formConfig?.user?.userId;
        const loginRole = state?.formConfig?.user?.role;
        // Prefer Redux profile; fall back to MSAL CWID for bootstrap calls (e.g. GET /users/{cwid})
        const userId = storeUserId || getCwidFromAccount(account);

        if (userId) {
          config.headers['userId'] = userId;
        }
        if (loginRole) {
          config.headers['userRole'] = loginRole;
        }
        config.headers['countryCode'] = COUNTRY_CODE;
      } else {
        console.warn('No accounts found. User might need to sign in.');
        await handleLogout();
        return Promise.reject(new Error('No authenticated user'));
      }
    } catch (error) {
      console.error('Failed to acquire token:', error);
      await handleLogout();
      return Promise.reject(error);
    }

    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // Check if the error is due to an expired token (401) or forbidden access (403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error("Token expired/invalid or access forbidden, logging out user");
        await handleLogout();
        return Promise.reject(new Error("Session expired or access denied"));
      }
      // Return the error for other status codes
      return Promise.reject(error);
    },
);

/**
 * Acquires the Azure AD access token from the same msalInstance used by the
 * request interceptor above, for consumers (e.g. tokenProvider callbacks)
 * that need the raw token outside of an axios request.
 */
export const getAccessToken = async (): Promise<string | undefined> => {
  try {
    await msalInitializedPromise;

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('No accounts found. User might need to sign in.');
      return undefined;
    }

    const tokenResponse = await msalInstance.acquireTokenSilent({
      account: accounts[0],
      scopes: [VITE_AZURE_CLIENTID + '/.default'],
    });

    return tokenResponse.accessToken;
  } catch (error) {
    console.error('Failed to acquire access token:', error);
    return undefined;
  }
};

export const FetchRetailers = `${API_URL}/retailers`;
export const SendOTP = `${API_URL}/otp/send`;
export const VerifyOTPs = `${API_URL}/otp/verify`;

export const get = async (
  endpoint: string,
  params?: Record<string, string>,
): Promise<any> => {
  try {
    const response = await axiosInstance.get(endpoint, {
      params,
    });
    console.log('GET request successful:', response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      console.error(`HTTP error ${status}:`, data);
      throw new Error(`HTTP error ${status}: ${JSON.stringify(data)}`);
    } else {
      console.error('An unexpected error occurred:', error);
      throw new Error('An unexpected error occurred.');
    }
  }
};

export const getBlob = async (
  endpoint: string,
  params?: Record<string, string>,
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(endpoint, {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status } = error.response;
      throw new Error(`HTTP error ${status}: Failed to download file`);
    } else {
      throw new Error('An unexpected error occurred.');
    }
  }
};

export const post = async (endpoint: string, data: any): Promise<any> => {
  try {
    const response = await axiosInstance.post(endpoint, data);
    console.log('POST request successful:', response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      if (data.message === 'Failed to create retailer' && status === 500) {
        console.error(
          'A survey is already under progress/completed for this user, please contact your Admin for more info',
        );
        return;
      } else if (status === 409) {
        return { ...data, status: status };
      } else {
        console.error('Something went wrong:', data);
      }
      throw new Error(data.message || 'Something went wrong!');
    } else {
      console.error('An unexpected error occurred:', error);
      throw new Error('An unexpected error occurred.');
    }
  }
};

export const put = async (endpoint: string, data: any): Promise<any> => {
  try {
    const response = await axiosInstance.put(endpoint, data);
    console.log('PUT request successful:', response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      console.error(`PUT request failed with status ${status}:`, data);
      throw new Error(
        data.message || 'Something went wrong with the PUT request.',
      );
    } else {
      console.error('An unexpected error occurred:', error);
      throw new Error('An unexpected error occurred.');
    }
  }
};

export const del = async (endpoint: string): Promise<any> => {
  try {
    const response = await axiosInstance.delete(endpoint);
    console.log('DELETE request successful:', response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      console.error(`DELETE request failed with status ${status}:`, data);
      throw new Error(
        data.message || 'Something went wrong with the DELETE request.',
      );
    } else {
      console.error('An unexpected error occurred:', error);
      throw new Error('An unexpected error occurred.');
    }
  }
};
